/*
 * Longeva Aesthetics collection page enhancer.
 *
 * The file-based collection template is not honoured by the
 * GitHub-bound live theme (it keeps serving the admin-stored
 * section+main layout), so the Selfridges-aligned header is delivered
 * by DOM enhancement instead, the same proven pattern as the PDP
 * enhancer / cart-recs / sitewide drawer.
 *
 * Adds, on collection pages only:
 *   1. A breadcrumb (Home / Collection) above the title (SEO + wayfinding).
 *   2. A clamped collection description with a Read more / Read less
 *      toggle (keeps full copy for SEO, uncluttered for UX).
 *
 * Companion CSS ships sitewide-inline in longeva-head-additions
 * (propagation-proof). Fully guarded, idempotent, no global overrides.
 */
(function () {
  "use strict";

  // Pathname is reliable at script-execution time; the body
  // `template-collection` class is added by Horizon JS LATER, so gating
  // the whole IIFE on it (with an early return) skipped the observer +
  // retries entirely. Gate per-run on the path instead, and always arm
  // the resilience machinery. Matches /collections/<handle> only (not
  // the /collections list).
  function onCollection() {
    try { return /^\/collections\/[^/]+/.test(location.pathname); }
    catch (e) { return false; }
  }

  function $(s, r) { return (r || document).querySelector(s); }
  function $all(s, r) { return [].slice.call((r || document).querySelectorAll(s)); }

  function mountBreadcrumb() {
    if ($(".longeva-col-breadcrumb")) return;
    var main = $("#MainContent") || $("main") || document.body;
    var h1 = main.querySelector("h1");
    if (!h1) return;
    var titleText = (h1.textContent || "").trim();
    if (!titleText) return;

    // Anchor: the text-block wrapping the H1, else the H1 itself.
    var anchor = h1;
    var tb = h1.closest('[class*="text-block"]');
    if (tb && tb.parentElement) anchor = tb;

    var nav = document.createElement("nav");
    nav.className = "longeva-col-breadcrumb";
    nav.setAttribute("aria-label", "Breadcrumb");

    var home = document.createElement("a");
    home.href = "/";
    home.textContent = "Home";

    var sep = document.createElement("span");
    sep.className = "longeva-col-breadcrumb__sep";
    sep.setAttribute("aria-hidden", "true");
    sep.textContent = "/";

    // Parent category from the menu IA (2026-05-22). Deterministic per
    // collection handle so the crumb is the same on every visit and for
    // crawlers, the SEO-correct way. Collections not in the menu get a
    // plain Home / Collection two-step, no invented parent.
    var TREAT = { name: 'Treatments', url: '/collections/all-products' };
    var CLINIC = { name: 'Clinic Essentials', url: '/collections/consumables' };
    var NAILS = { name: 'Nails', url: '/collections/nails' };
    var SKIN = { name: 'Skincare', url: '/collections/skincare' };
    var PARENT_MAP = {
      'dermal-fillers': TREAT, 'skin-boosters': TREAT, 'polynucleotides': TREAT,
      'fat-dissolvers': TREAT, 'pdo-threads': TREAT, 'body-contouring': TREAT,
      'rhinoplasty': TREAT, 'skin-needling': TREAT, 'microblading-spmu': TREAT,
      'pre-treatment': CLINIC, 'post-treatment': CLINIC, 'sanitisation': CLINIC,
      'gloves-ppe': CLINIC, 'gauze-wipes': CLINIC, 'needles-syringes-1': CLINIC,
      'sharps-disposal': CLINIC,
      'nail-tips': NAILS, 'acrylics-powders': NAILS, 'gel-polish': NAILS,
      'builder-gel': NAILS, 'nail-tools-accessories': NAILS, 'nail-art': NAILS,
      'removers-aftercare': NAILS,
      'cleansers': SKIN, 'exfoliators': SKIN, 'serums': SKIN, 'toners': SKIN,
      'spf': SKIN, 'modeling-face-masks': SKIN, 'post-treatment-skincare': SKIN
    };
    var handle = (location.pathname.split('/collections/')[1] || '').split('/')[0].split('?')[0];
    var parent = PARENT_MAP[handle] || null;

    var cur = document.createElement("span");
    cur.className = "longeva-col-breadcrumb__current";
    cur.setAttribute("aria-current", "page");
    cur.textContent = titleText;

    nav.appendChild(home);
    nav.appendChild(sep);
    if (parent) {
      var pa = document.createElement("a");
      pa.href = parent.url;
      pa.textContent = parent.name;
      var sep2 = document.createElement("span");
      sep2.className = "longeva-col-breadcrumb__sep";
      sep2.setAttribute("aria-hidden", "true");
      sep2.textContent = "/";
      nav.appendChild(pa);
      nav.appendChild(sep2);
    }
    nav.appendChild(cur);

    if (anchor.parentElement) anchor.parentElement.insertBefore(nav, anchor);
  }

  function mountReadMore() {
    var main = $("#MainContent") || $("main") || document.body;
    var h1 = main.querySelector("h1");
    var h1Block = h1 ? h1.closest('[class*="text-block"]') : null;

    // The collection description: the richest text-block in the header
    // area that is not the title and sits before the product grid.
    var grid =
      main.querySelector(
        '[class*="product-grid"], .product-grid, .collection .grid, ul[class*="product"]'
      ) || null;
    var blocks = $all('[class*="text-block"], .rte, rte-formatter', main).filter(
      function (el) {
        if (h1Block && (el === h1Block || h1Block.contains(el) || el.contains(h1Block))) return false;
        if (el.querySelector("h1")) return false;
        if (el.classList.contains("longeva-col-desc")) return false;
        var t = (el.textContent || "").trim();
        if (t.length < 120) return false;
        if (grid && (grid.contains(el) || el.contains(grid))) return false;
        // must appear before the grid in document order
        if (grid && (el.compareDocumentPosition(grid) & Node.DOCUMENT_POSITION_FOLLOWING) === 0) return false;
        return true;
      }
    );
    if (!blocks.length) return;
    var src = blocks[0];
    if (src.dataset.lvDesc === "1") return;
    src.dataset.lvDesc = "1";

    // Horizon renders the description as an <rte-formatter> custom
    // element inside a flex layout panel. As a flex child it is sized
    // to ~0 width, so its copy wraps into a 7000px column behind the
    // clip and the element renders ~1px tall. Restyling that custom
    // element in place loses every time. Extract the copy into a plain
    // block we own (the nodes are MOVED, so there is a single copy and
    // no duplicate text for SEO), drop it into normal flow beside the
    // title (which lays out correctly), and hide the emptied original.
    // Same controlled-DOM pattern as the breadcrumb.
    var desc = document.createElement("div");
    desc.className = "longeva-col-desc";
    while (src.firstChild) desc.appendChild(src.firstChild);

    var anchor = src;
    if (h1Block && h1Block.parentElement) anchor = h1Block;
    if (anchor.parentElement) {
      anchor.parentElement.insertBefore(desc, anchor.nextSibling);
    } else if (src.parentElement) {
      src.parentElement.insertBefore(desc, src);
    }
    src.style.display = "none";

    // Collapse to ~3 lines using a concrete pixel value (a max-height
    // keyword never transitions, which was the original bug).
    var lh = parseFloat(getComputedStyle(desc).lineHeight);
    if (!lh || isNaN(lh)) lh = 22;
    var collapsed = Math.round(lh * 3);

    // Only surface the control when the copy actually overflows. The
    // M&S reference shows Read more solely when there is more to reveal.
    if (desc.scrollHeight <= collapsed + 8) {
      desc.style.maxHeight = "";
      return;
    }
    desc.style.maxHeight = collapsed + "px";

    var btn = document.createElement("button");
    btn.type = "button";
    btn.className = "longeva-col-readmore";
    btn.setAttribute("aria-expanded", "false");
    btn.textContent = "Read more";

    var open = false;
    btn.addEventListener("click", function () {
      open = !open;
      if (open) {
        desc.style.maxHeight = desc.scrollHeight + "px";
        btn.textContent = "Read less";
        btn.setAttribute("aria-expanded", "true");
        // After the grow settles, drop the cap so responsive reflow
        // (resize / font swap) is never clipped.
        var onEnd = function (e) {
          if (e.propertyName !== "max-height") return;
          if (open) desc.style.maxHeight = "none";
          desc.removeEventListener("transitionend", onEnd);
        };
        desc.addEventListener("transitionend", onEnd);
      } else {
        // From the uncapped state, pin to the current height then to
        // the collapsed value so the close animates instead of jumping.
        desc.style.maxHeight = desc.scrollHeight + "px";
        void desc.offsetHeight;
        desc.style.maxHeight = collapsed + "px";
        btn.textContent = "Read more";
        btn.setAttribute("aria-expanded", "false");
      }
    });
    if (desc.parentElement) {
      if (desc.nextSibling) desc.parentElement.insertBefore(btn, desc.nextSibling);
      else desc.parentElement.appendChild(btn);
    }
  }

  // Sold-out products to the bottom of the collection grid (Varsha 2 June).
  // Shopify's Search and Discovery out-of-stock demotion applies to search
  // results but not to collection pages that carry an explicit A-Z sort, so
  // sold-out items render wherever they fall alphabetically. This keeps the
  // A-Z order and pushes each sold-out card to the end of the grid via the
  // flex/grid `order` property (in-stock stay alphabetical, sold-out cluster
  // alphabetically after them). Class plus inline order, idempotent, no DOM
  // move (so Horizon load-more and filtering keep working).
  function findGrid() {
    var main = document.querySelector("#MainContent") || document.querySelector("main") || document.body;
    // The real layout container is <ul class="product-grid"> (display:grid).
    // Match by class token, not substring, so the display:block
    // "product-grid-container" wrapper is not picked by mistake. Confirm the
    // element is an actual flex/grid container that holds product cards.
    var grids = $all("ul.product-grid, .product-grid", main);
    for (var i = 0; i < grids.length; i++) {
      var d = "";
      try { d = getComputedStyle(grids[i]).display; } catch (e) {}
      var laysOut = d === "grid" || d === "inline-grid" || d === "flex" || d === "inline-flex";
      if (laysOut && grids[i].querySelector('product-card, [class*="product-card"]')) return grids[i];
    }
    return grids[0] || null;
  }
  function isSoldOut(card) {
    var badges = $all('.product-badges__badge, [class*="badge"]', card);
    for (var i = 0; i < badges.length; i++) {
      if (/sold\s*out/i.test((badges[i].textContent || "").trim())) return true;
    }
    var btn = card.querySelector(
      'add-to-cart-component button, button[name="add"], [class*="add-to-cart"] button, .add-to-cart-text'
    );
    if (btn && /sold\s*out/i.test(btn.textContent || "")) return true;
    return false;
  }
  function sortSoldOutLast() {
    if (!onCollection()) return;
    var grid = findGrid();
    if (!grid) return;
    var cards = $all("product-card", grid);
    if (!cards.length) cards = $all('[class*="product-card"]', grid);
    cards.forEach(function (card) {
      // walk up to the grid's direct child (handles an <li>/<div> wrapper)
      var item = card;
      while (item.parentElement && item.parentElement !== grid) item = item.parentElement;
      if (!item.parentElement || item.parentElement !== grid) return;
      if (isSoldOut(card)) {
        if (!item.classList.contains("longeva-soldout-last")) {
          item.classList.add("longeva-soldout-last");
          item.style.order = "100";
        }
      }
    });
  }

  var running = false;
  function run() {
    if (running || !onCollection()) return;
    running = true;
    try { mountBreadcrumb(); } catch (e) {}
    try { mountReadMore(); } catch (e) {}
    try { sortSoldOutLast(); } catch (e) {}
    running = false;
  }

  function start() {
    run();
    // Horizon hydrates / morphs the collection section after first
    // paint, which can land after our injection or wipe it. Re-run on
    // DOM changes (guarded + idempotent: each mount no-ops once its
    // node exists, so this cannot loop) plus staggered retries.
    var main =
      document.querySelector("#MainContent") ||
      document.querySelector("main") ||
      document.body;
    try {
      var t = null;
      var mo = new MutationObserver(function () {
        if (
          document.querySelector(".longeva-col-breadcrumb") &&
          document.querySelector(".longeva-col-desc")
        ) return;
        clearTimeout(t);
        t = setTimeout(run, 120);
      });
      mo.observe(main, { childList: true, subtree: true });
    } catch (e) {}
    // Dedicated observer for sold-out sorting: re-applies after Horizon
    // filters or load-more swap the grid. Watches childList only, and
    // sortSoldOutLast only toggles a class plus inline order (attribute
    // changes), so this cannot loop.
    try {
      var st = null;
      var smo = new MutationObserver(function () {
        clearTimeout(st);
        st = setTimeout(function () { try { sortSoldOutLast(); } catch (e) {} }, 150);
      });
      smo.observe(main, { childList: true, subtree: true });
    } catch (e) {}
    [400, 1200, 2500, 4500].forEach(function (ms) { setTimeout(run, ms); });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start, { once: true });
  } else {
    start();
  }
})();
